import { Component, type ReactNode } from 'react';
import { tokens } from '@fluentui/react-components';
import { logErrorBoundary } from '../lib/errorHandler';
import { logger } from '../lib/logger';
import { teamsAuth } from '../lib/teams-auth';

type State = { hasError: boolean; message?: string; errorId?: string };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };
  
  static getDerivedStateFromError(err: any): State {
    // Generate unique error ID for tracking
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return { 
      hasError: true, 
      message: err?.message || String(err),
      errorId
    };
  }
  
  componentDidCatch(err: any, info: any) {
    logger.error('ErrorBoundary caught error', { error: err, info, errorId: this.state.errorId }, 'ERROR_BOUNDARY');
    // A render crash unmounts the tree before AuthProvider's effect runs, so the
    // M365 host's loading indicator would never be dismissed and the fallback UI
    // below would stay hidden behind it — the app would look hung rather than
    // broken. Idempotent: a no-op if the handshake already reported.
    teamsAuth.releaseLoadingIndicator(false);
    try {
      logErrorBoundary(err, info, 'ErrorBoundary');
    } catch (logErr) {
      logger.error('Failed to log error to handler', logErr, 'ERROR_BOUNDARY');
    }
  }
  render() {
    if (this.state.hasError) {
      return <div 
        style={{
          padding: tokens.spacingVerticalXXL
        }}
      >
        <h2 
          style={{
            fontSize: tokens.fontSizeBase500,
            fontWeight: tokens.fontWeightSemibold,
            marginBottom: tokens.spacingVerticalS,
            color: tokens.colorNeutralForeground1,
            fontFamily: tokens.fontFamilyBase
          }}
        >
          Something went wrong
        </h2>
        <p 
          style={{
            fontSize: tokens.fontSizeBase300,
            color: tokens.colorNeutralForeground2,
            marginBottom: tokens.spacingVerticalM,
            fontFamily: tokens.fontFamilyBase
          }}
        >
          An unexpected error occurred. The error has been logged and our team has been notified.
        </p>
        <details 
          style={{
            fontSize: tokens.fontSizeBase200,
            backgroundColor: tokens.colorPaletteRedBackground1,
            padding: tokens.spacingVerticalM,
            borderRadius: tokens.borderRadiusXLarge,
            border: `1px solid ${tokens.colorPaletteRedBorder1}`,
            fontFamily: tokens.fontFamilyBase
          }}
        >
          <summary style={{ cursor: 'pointer', marginBottom: tokens.spacingVerticalS }}>
            Error Details (Error ID: {this.state.errorId})
          </summary>
          <pre 
            style={{
              whiteSpace: 'pre-wrap',
              color: tokens.colorPaletteRedForeground1,
              fontFamily: tokens.fontFamilyMonospace,
              margin: 0,
              fontSize: tokens.fontSizeBase100
            }}
          >
            {this.state.message}
          </pre>
        </details>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: tokens.spacingVerticalM,
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            backgroundColor: tokens.colorBrandBackground,
            color: tokens.colorNeutralForegroundOnBrand,
            border: 'none',
            borderRadius: tokens.borderRadiusMedium,
            cursor: 'pointer',
            fontSize: tokens.fontSizeBase200,
            fontFamily: tokens.fontFamilyBase
          }}
        >
          Refresh Page
        </button>
      </div>;
    }
    return this.props.children;
  }
}
